/**
 * WebSocket Command Service — Browser fallback for MQTT
 *
 * When running in a browser (not native Android), MQTT may not be available.
 * This service connects via socket.io to the API's /resident namespace
 * and listens for device.command events, feeding them into the same
 * command pipeline as MQTT.
 */
import { io, Socket } from 'socket.io-client';
import { dispatchCommand } from './MqttTelemetry';
import type { MqttCommand } from './MqttTelemetry';

const API_URL = (import.meta as any).env?.VITE_API_URL || '';

let socket: Socket | null = null;
let currentUserId: number | null = null;

/**
 * Live domotica-toestand.
 *
 * ── WAAROM OP DEZE SOCKET EN NIET EEN EIGEN ─────────────────────────────────
 *
 * De API zendt `domotics.state` op `/resident`, in de kamer `unit:<id>`. Het toestel
 * wordt daar bij het verbinden zelf in gezet, afgeleid uit zijn DeviceToken — de client
 * kan die kamer niet noemen, en dat is opzet.
 *
 * `middleware/wsAuth.js` weigert device-tokens op ELKE andere namespace. Een eigen
 * socket vanuit de domoticapagina kwam er dus niet in, en luisterde bovendien naar een
 * gebeurtenisnaam die nergens in de API bestaat. Resultaat: elke tegel bleef op
 * "verzonden" staan, en omdat er bewust geen timeout naar "gelukt" is, permanent.
 *
 * Eén socket, meerdere luisteraars. Een tweede verbinding zou een tweede kamerlidmaatschap
 * betekenen en dus dubbele events.
 */
export interface DomoticsStateEvent {
  id: number;
  value: boolean | number;
  previousValue?: boolean | number;
  inverted?: boolean;
  lastStateChange?: string;
}

const domoticsListeners = new Set<(e: DomoticsStateEvent) => void>();

/** Abonneer op live domotica-toestand. Geeft de opzegfunctie terug. */
export function onDomoticsState(cb: (e: DomoticsStateEvent) => void): () => void {
  domoticsListeners.add(cb);
  return () => { domoticsListeners.delete(cb); };
}

/**
 * Zorg dat de gedeelde /resident-socket er is.
 *
 * De domoticapagina kan geopend worden voordat useMqttTelemetry heeft verbonden (of op
 * een toestel waar die haak helemaal niet loopt). Zonder dit zou de pagina dan stil geen
 * live toestand hebben — en dat ziet er precies uit als een werkende pagina.
 */
export function ensureResidentSocket(getToken: () => string | null) {
  if (socket) return;
  connectWebSocketCommands({ userId: 0, getToken });
}

export interface WsCommandConfig {
  userId: number;
  getToken: () => string | null;
}

export function connectWebSocketCommands(config: WsCommandConfig) {
  if (socket) return; // Already connected
  if (!API_URL) {
    console.warn('[WsCommands] No API URL configured, skipping WebSocket connection');
    return;
  }

  currentUserId = config.userId;
  console.log('[WsCommands] Connecting to', API_URL, '/resident as user', config.userId);

  socket = io(`${API_URL}/resident`, {
    transports: ['websocket', 'polling'],
    auth: {
      token: config.getToken(),
    },
    reconnection: true,
    reconnectionDelay: 3000,
    reconnectionDelayMax: 15000,
  });

  socket.on('connect', () => {
    // `ensureResidentSocket` verbindt zonder persoon (userId 0) wanneer alleen de
    // domoticapagina hem nodig heeft. De unit-kamer wordt server-side toegekend uit het
    // DeviceToken en heeft geen subscribe nodig; de persoonskamer wel.
    if (config.userId) {
      console.log('[WsCommands] Connected, subscribing to user room');
      socket!.emit('subscribe', { personId: config.userId });
    } else {
      console.log('[WsCommands] Connected (unit room only)');
    }
  });

  socket.on('device.command', (data: any) => {
    try {
      console.log('[WsCommands] Command received:', data);
      const cmd: MqttCommand = {
        id: data.id || `ws-${Date.now()}`,
        type: data.type,
        payload: data.payload || {},
        timestamp: data.timestamp || new Date().toISOString(),
        source: data.source || 'websocket',
      };

      if (!cmd.type) {
        console.warn('[WsCommands] Invalid command (missing type):', data);
        return;
      }

      dispatchCommand(cmd);
    } catch (e) {
      console.error('[WsCommands] Error processing command:', e);
    }
  });

  // De server vertaalt de waarde al naar bewonerssemantiek (`toResident`), net als het
  // lijst-endpoint. Hier dus niets omrekenen: twee plekken die dat doen gaan uit elkaar
  // lopen, en dan verschilt een live update van een pagina-verversing.
  socket.on('domotics.state', (data: any) => {
    if (!data || typeof data.id !== 'number') return;
    for (const cb of domoticsListeners) {
      try { cb(data as DomoticsStateEvent); } catch { /* één luisteraar mag de rest niet slopen */ }
    }
  });

  socket.on('disconnect', (reason) => {
    console.log('[WsCommands] Disconnected:', reason);
  });

  socket.on('connect_error', (err) => {
    console.warn('[WsCommands] Connection error:', err.message);
  });
}

export function disconnectWebSocketCommands() {
  if (socket) {
    if (currentUserId) {
      socket.emit('unsubscribe', { personId: currentUserId });
    }
    socket.disconnect();
    socket = null;
    currentUserId = null;
    console.log('[WsCommands] Disconnected');
  }
}
